import {Injectable} from '@angular/core';
import {SourceTotalsByTypeInterface} from '@core/services/source/source.service';
import {BehaviorSubject, OperatorFunction, Subscription} from 'rxjs';
import {Store} from '@ngrx/store';
import {LibrarySelectors} from '@store/library';
import {filter} from 'rxjs/operators';
import * as _ from 'lodash';

export enum SourceExtensionEnum {
    Xsl,
    Pdf,
    Ppt,
    Link,
    Doc,
    Other,
    Mine,
    Img,
}

export interface SourceExtensionInterface {
    identifier: string;
    mapping: string[];
    count: number;
}

@Injectable({
    providedIn: 'root',
})
export class SourceExtensionService {
    #sourceTotalsByType?: SourceTotalsByTypeInterface;

    #defaultSourceTypeMapping: Map<SourceExtensionEnum, SourceExtensionInterface> = new Map([
        [SourceExtensionEnum.Doc, {count: 0, identifier: 'doc', mapping: ['doc', 'docx']}],
        [SourceExtensionEnum.Img, {count: 0, identifier: 'img', mapping: ['png', 'jpg', 'jpeg']}],
        [SourceExtensionEnum.Link, {count: 0, identifier: 'url', mapping: ['url', 'link']}],
        [SourceExtensionEnum.Pdf, {count: 0, identifier: 'pdf', mapping: ['pdf']}],
        [SourceExtensionEnum.Ppt, {count: 0, identifier: 'ppt', mapping: ['ppt', 'pptx']}],
        [SourceExtensionEnum.Xsl, {count: 0, identifier: 'xsl', mapping: ['xsl', 'xlsx']}],
        [SourceExtensionEnum.Mine, {count: 0, identifier: 'mine', mapping: ['mine']}],
        [SourceExtensionEnum.Other, {count: 0, identifier: 'other', mapping: []}],
    ]);

    #multiSelectData: BehaviorSubject<Map<SourceExtensionEnum, SourceExtensionInterface> | undefined> = new BehaviorSubject<Map<SourceExtensionEnum, SourceExtensionInterface> | undefined>(undefined);

    constructor(store: Store) {
        store.select(LibrarySelectors.selectDocumentSourceDataTotalsByType)
            .pipe(filter(totalsByType => undefined !== totalsByType && this.#hasChanged(totalsByType)) as OperatorFunction<SourceTotalsByTypeInterface | undefined, SourceTotalsByTypeInterface>)
            .subscribe(totalsByType => this.#handleLibrarySelectSourceExtensions(totalsByType));
    }

    subscribe(fn: (mappedSourceExtensions: Map<SourceExtensionEnum, SourceExtensionInterface>) => void): Subscription {
        return this.#multiSelectData
            .pipe(filter(extensions => undefined !== extensions) as OperatorFunction<Map<SourceExtensionEnum, SourceExtensionInterface> | undefined, Map<SourceExtensionEnum, SourceExtensionInterface>>)
            .subscribe(extensions => fn(extensions));
    }

    #handleLibrarySelectSourceExtensions(sourceTotalsByType: SourceTotalsByTypeInterface): void {
        const mappedSourceExtensions: Map<SourceExtensionEnum, SourceExtensionInterface> = _.clone(this.#defaultSourceTypeMapping);

        Object.keys(sourceTotalsByType).forEach(type => {
            const extension: SourceExtensionEnum = this.#getMappedSourceExtension(type);
            const mappedSourceExtension: SourceExtensionInterface = <SourceExtensionInterface>mappedSourceExtensions.get(extension);

            // In case we're handling "other", add that extension to mapping when it's not included yet
            if (SourceExtensionEnum.Other === extension && !mappedSourceExtension.mapping.includes(type)) {
                mappedSourceExtension.mapping.push(type);
            }

            mappedSourceExtension.count += sourceTotalsByType[type];
        });

        this.#multiSelectData.next(mappedSourceExtensions);
    }

    #getMappedSourceExtension(type: string): SourceExtensionEnum {
        let toType: SourceExtensionEnum = SourceExtensionEnum.Other;

        // Check if extension is mapped
        for (const [key, value] of this.#defaultSourceTypeMapping) {
            if (value.mapping.includes(type)) {
                return key;
            }
        }

        return toType;
    }

    #hasChanged(sourceTotalsByType: SourceTotalsByTypeInterface): boolean {
        const previousSourceTotalsByType: SourceTotalsByTypeInterface | undefined = this.#sourceTotalsByType;
        const isSame = _.isEqual(previousSourceTotalsByType, this.#sourceTotalsByType = sourceTotalsByType);

        return !isSame;
    }
}
